/* Kiwi Ears · dot-wave background (Rene 2026-08-27).
   Vanilla WebGL2 port of the "Chromatic Waves" (Originkit DottedBg2) React/OGL
   component: a simplex-noise field rendered to a texture, then sampled into a
   halftone dot matrix — dot size and colour follow the field's luminance
   through a palette. The React/ogl dependency is gone; it is two fullscreen
   quads and a framebuffer.

   Arms every [data-dotwave] element (position:relative/absolute parent):
     data-dotwave-colors  space-separated hex palette, dark → bright ("#173328 #00E58A")
     data-dotwave-freq    1-10   noise frequency   (default 1)
     data-dotwave-speed   0-20   animation speed   (default 7)
     data-dotwave-cell    1-100  dot cell size     (default 19)
     data-dotwave-gamma   1-20   luminance gamma   (default 5)
     data-dotwave-bias    ±20    dot size bias     (default -1)

   Guards the component did not have: an IntersectionObserver stops the loop
   whenever the element is offscreen (a footer is offscreen almost always),
   device pixel ratio is capped at 1.5, the loop is capped at 30fps, and
   prefers-reduced-motion renders one static frame instead of animating. */
(function () {
  'use strict';

  var VERT =
    '#version 300 es\n' +
    'in vec2 uv; in vec2 position; out vec2 vUv;\n' +
    'void main(){ vUv = uv; gl_Position = vec4(position, 0., 1.); }';

  var PERLIN_FRAG =
    '#version 300 es\n' +
    'precision mediump float;\n' +
    'uniform float uFrequency; uniform float uTime; uniform float uSpeed; uniform float uValue;\n' +
    'uniform vec2 uResolution; in vec2 vUv; out vec4 fragColor;\n' +
    'vec3 mod289(vec3 x){ return x - floor(x * (1.0/289.0)) * 289.0; }\n' +
    'vec4 mod289(vec4 x){ return x - floor(x * (1.0/289.0)) * 289.0; }\n' +
    'vec4 permute(vec4 x){ return mod289(((x*34.0)+1.0)*x); }\n' +
    'vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }\n' +
    'float snoise(vec3 v){\n' +
    '  const vec2 C = vec2(1.0/6.0, 1.0/3.0); const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);\n' +
    '  vec3 i = floor(v + dot(v, C.yyy)); vec3 x0 = v - i + dot(i, C.xxx);\n' +
    '  vec3 g = step(x0.yzx, x0.xyz); vec3 l = 1.0 - g;\n' +
    '  vec3 i1 = min(g.xyz, l.zxy); vec3 i2 = max(g.xyz, l.zxy);\n' +
    '  vec3 x1 = x0 - i1 + C.xxx; vec3 x2 = x0 - i2 + C.yyy; vec3 x3 = x0 - D.yyy;\n' +
    '  i = mod289(i);\n' +
    '  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0))\n' +
    '         + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));\n' +
    '  float n_ = 0.142857142857; vec3 ns = n_ * D.wyz - D.xzx;\n' +
    '  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);\n' +
    '  vec4 x_ = floor(j * ns.z); vec4 y_ = floor(j - 7.0 * x_);\n' +
    '  vec4 x = x_ * ns.x + ns.yyyy; vec4 y = y_ * ns.x + ns.yyyy;\n' +
    '  vec4 h = 1.0 - abs(x) - abs(y);\n' +
    '  vec4 b0 = vec4(x.xy, y.xy); vec4 b1 = vec4(x.zw, y.zw);\n' +
    '  vec4 s0 = floor(b0)*2.0 + 1.0; vec4 s1 = floor(b1)*2.0 + 1.0;\n' +
    '  vec4 sh = -step(h, vec4(0.0));\n' +
    '  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy; vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;\n' +
    '  vec3 p0 = vec3(a0.xy, h.x); vec3 p1 = vec3(a0.zw, h.y);\n' +
    '  vec3 p2 = vec3(a1.xy, h.z); vec3 p3 = vec3(a1.zw, h.w);\n' +
    '  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));\n' +
    '  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;\n' +
    '  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);\n' +
    '  m = m * m;\n' +
    '  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));\n' +
    '}\n' +
    'vec3 hsv2rgb(vec3 c){\n' +
    '  vec4 K = vec4(1.0, 2.0/3.0, 1.0/3.0, 3.0);\n' +
    '  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);\n' +
    '  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);\n' +
    '}\n' +
    'void main(){\n' +
    '  vec2 uv = vUv;\n' +
    '  float aspect = uResolution.x / max(uResolution.y, 1.0);\n' +
    '  uv = (uv - 0.5) * vec2(aspect, 1.0) + 0.5;\n' +
    '  float hue = abs(snoise(vec3(uv * uFrequency, uTime * uSpeed)));\n' +
    '  fragColor = vec4(hsv2rgb(vec3(hue, 1.0, uValue)), 1.0);\n' +
    '}';

  var DOT_FRAG =
    '#version 300 es\n' +
    'precision highp float;\n' +
    'uniform vec2 uResolution; uniform sampler2D uTexture;\n' +
    'uniform int uPaletteCount; uniform vec3 uPalette[10]; uniform float uPaletteAlpha[10];\n' +
    'uniform float uCellSize; uniform float uGamma; uniform float uPaletteBias;\n' +
    'out vec4 fragColor;\n' +
    'void main(){\n' +
    '  vec2 pix = gl_FragCoord.xy;\n' +
    '  float cell = max(uCellSize, 1.0);\n' +
    '  vec2 cellIdx = floor(pix / cell);\n' +
    '  vec2 cellCenter = (cellIdx + 0.5) * cell;\n' +
    '  vec3 col = texture(uTexture, cellCenter / uResolution.xy).rgb;\n' +
    '  float gray = 0.3 * col.r + 0.59 * col.g + 0.11 * col.b;\n' +
    '  gray = pow(clamp(gray, 0.0001, 1.0), uGamma);\n' +
    '  vec2 cellUV = fract(pix / cell) - 0.5;\n' +
    '  float dist = length(cellUV);\n' +
    '  float radius = clamp(gray + uPaletteBias, 0.0, 1.0) * 0.5;\n' +
    '  float aa = fwidth(dist) + 1e-4;\n' +
    '  float mark = 1.0 - smoothstep(radius - aa, radius + aa, dist);\n' +
    '  float g2 = clamp(gray + uPaletteBias, 0.0, 1.0);\n' +
    '  int cnt = max(uPaletteCount, 1);\n' +
    '  vec3 dotCol; float dotOpacity;\n' +
    '  if (cnt <= 1) { dotCol = uPalette[0]; dotOpacity = uPaletteAlpha[0]; }\n' +
    '  else {\n' +
    '    float scaled = g2 * float(cnt - 1);\n' +
    '    int seg = clamp(int(floor(scaled)), 0, cnt - 2);\n' +
    '    float f = clamp(scaled - float(seg), 0.0, 1.0);\n' +
    '    dotCol = mix(uPalette[seg], uPalette[seg + 1], f);\n' +
    '    dotOpacity = mix(uPaletteAlpha[seg], uPaletteAlpha[seg + 1], f);\n' +
    '  }\n' +
    '  fragColor = vec4(dotCol, mark * dotOpacity);\n' +
    '}';

  function mapLinear(v, a, b, c, d) { return b === a ? c : c + ((v - a) / (b - a)) * (d - c); }

  function parseHex(str) {
    var h = (str || '').trim().replace(/^#/, '');
    function b(s) { return parseInt(s, 16) / 255; }
    if (h.length === 3) return [b(h[0] + h[0]), b(h[1] + h[1]), b(h[2] + h[2]), 1];
    if (h.length === 4) return [b(h[0] + h[0]), b(h[1] + h[1]), b(h[2] + h[2]), b(h[3] + h[3])];
    if (h.length === 6) return [b(h.slice(0, 2)), b(h.slice(2, 4)), b(h.slice(4, 6)), 1];
    if (h.length === 8) return [b(h.slice(0, 2)), b(h.slice(2, 4)), b(h.slice(4, 6)), b(h.slice(6, 8))];
    return [1, 1, 1, 1];
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
    return s;
  }

  function link(gl, vsSrc, fsSrc) {
    var vs = compile(gl, gl.VERTEX_SHADER, vsSrc);
    var fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc);
    if (!vs || !fs) return null;
    var p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return null;
    return p;
  }

  function arm(el) {
    if (el.__dotwave) return;
    el.__dotwave = true;

    var ds = el.dataset;
    var freq = mapLinear(parseFloat(ds.dotwaveFreq || '1'), 1, 10, 0.3, 6);
    var speed = parseFloat(ds.dotwaveSpeed || '7') * 0.05;
    var cell = mapLinear(parseFloat(ds.dotwaveCell || '19'), 1, 100, 6, 60);
    var gamma = mapLinear(parseFloat(ds.dotwaveGamma || '5'), 1, 20, 0.5, 8);
    var bias = parseFloat(ds.dotwaveBias || '-1') * 0.05;
    var colorList = (ds.dotwaveColors || '#173328 #00E58A').split(/\s+/).filter(Boolean).slice(0, 10);

    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    canvas.setAttribute('aria-hidden', 'true');
    var gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: false, antialias: false });
    if (!gl) return; /* no WebGL2 → the band keeps its plain ground */
    el.appendChild(canvas);

    var perlinProg = link(gl, VERT, PERLIN_FRAG);
    var dotProg = link(gl, VERT, DOT_FRAG);
    if (!perlinProg || !dotProg) return;

    /* fullscreen quad, shared by both passes */
    var quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1
    ]), gl.STATIC_DRAW);
    function bindQuad(prog) {
      var pos = gl.getAttribLocation(prog, 'position');
      var uv = gl.getAttribLocation(prog, 'uv');
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(pos);
      gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 16, 0);
      if (uv >= 0) {
        gl.enableVertexAttribArray(uv);
        gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);
      }
    }

    /* noise pass render target */
    var tex = gl.createTexture();
    var fbo = gl.createFramebuffer();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    var U = { p: {}, d: {} };
    ['uTime', 'uFrequency', 'uSpeed', 'uValue', 'uResolution'].forEach(function (n) {
      U.p[n] = gl.getUniformLocation(perlinProg, n);
    });
    ['uResolution', 'uTexture', 'uPaletteCount', 'uCellSize', 'uGamma', 'uPaletteBias'].forEach(function (n) {
      U.d[n] = gl.getUniformLocation(dotProg, n);
    });
    U.d.uPalette = gl.getUniformLocation(dotProg, 'uPalette[0]') || gl.getUniformLocation(dotProg, 'uPalette');
    U.d.uPaletteAlpha = gl.getUniformLocation(dotProg, 'uPaletteAlpha[0]') || gl.getUniformLocation(dotProg, 'uPaletteAlpha');

    var rgb = new Float32Array(30);
    var alpha = new Float32Array(10);
    colorList.forEach(function (c, i) {
      var v = parseHex(c);
      rgb[i * 3] = v[0]; rgb[i * 3 + 1] = v[1]; rgb[i * 3 + 2] = v[2];
      alpha[i] = v[3];
    });

    var w = 0, h = 0;
    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var cw = Math.max(1, Math.round((el.clientWidth || 1) * dpr));
      var chh = Math.max(1, Math.round((el.clientHeight || 1) * dpr));
      if (cw === w && chh === h) return false;
      w = cw; h = chh;
      canvas.width = w; canvas.height = h;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return true;
    }

    function draw(timeSec) {
      gl.viewport(0, 0, w, h);
      gl.disable(gl.BLEND);
      /* pass 1: noise field → texture */
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.useProgram(perlinProg);
      bindQuad(perlinProg);
      gl.uniform1f(U.p.uTime, timeSec);
      gl.uniform1f(U.p.uFrequency, freq);
      gl.uniform1f(U.p.uSpeed, speed);
      gl.uniform1f(U.p.uValue, 1);
      gl.uniform2f(U.p.uResolution, w, h);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      /* pass 2: dot matrix → canvas */
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(dotProg);
      bindQuad(dotProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(U.d.uTexture, 0);
      gl.uniform2f(U.d.uResolution, w, h);
      gl.uniform1i(U.d.uPaletteCount, colorList.length);
      gl.uniform3fv(U.d.uPalette, rgb);
      gl.uniform1fv(U.d.uPaletteAlpha, alpha);
      gl.uniform1f(U.d.uCellSize, cell);
      gl.uniform1f(U.d.uGamma, gamma);
      gl.uniform1f(U.d.uPaletteBias, bias);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var visible = false;
    var rafId = null;
    var last = 0;
    var FRAME = 1000 / 30;

    function loop(t) {
      rafId = null;
      if (!visible || (reduceMq && reduceMq.matches)) return;
      if (t - last >= FRAME) {
        last = t;
        draw(t * 0.001);
      }
      rafId = requestAnimationFrame(loop);
    }
    function play() {
      if (rafId == null) { last = 0; rafId = requestAnimationFrame(loop); }
    }
    function still() { resize(); draw(3.7); } /* a fixed time with a pleasant composition */

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          visible = e.isIntersecting;
          if (visible) {
            if (reduceMq && reduceMq.matches) { still(); } else { play(); }
          }
        });
      }, { rootMargin: '100px' }).observe(el);
    } else {
      visible = true;
      if (reduceMq && reduceMq.matches) { still(); } else { play(); }
    }

    if (reduceMq && reduceMq.addEventListener) {
      reduceMq.addEventListener('change', function () {
        if (!visible) return;
        if (reduceMq.matches) { if (rafId) { cancelAnimationFrame(rafId); rafId = null; } still(); }
        else { play(); }
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      var pending = false;
      new ResizeObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          if (resize() && (rafId == null)) draw(3.7);
        });
      }).observe(el);
    }
    resize();
  }

  function init() {
    document.querySelectorAll('[data-dotwave]').forEach(arm);
  }
  if (document.readyState !== 'loading') { init(); }
  else { document.addEventListener('DOMContentLoaded', init); }
})();
